import { useDeviceType } from "@dash/mq";
import { HeaderBar, Page, SectionHeader } from "@dash/page";
import { QuerySkeleton } from "@dash/skeleton";
import { useNavigate, useParams } from "react-router-dom-v5-compat";

import { useClientManager } from "@megaron/react-clients";
import { TicketDetailDto } from "@megaron/support-contracts";
import { Uuid } from "@megaron/uuid";

import { SupportTicketMessagesAndNotes } from "../messages/SupportTicketMessagesAndNotes";
import { SupportTicketActiveCartCard } from "../ticket/SupportTicketActiveCartCard";
import { SupportTicketContactCrmCard } from "../ticket/SupportTicketContactCrmCard";
import { SupportTicketHeaderActions } from "../ticket/SupportTicketHeaderActions";
import { SupportTicketMetaSection } from "../ticket/SupportTicketMetaSection";
import { getReporterCustomerUuidFromTicket } from "../supportTicketCustomerUtils";

export const SupportTicketPage = ({ listPath = "/support/tickets" }: { listPath?: string }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { isMobile } = useDeviceType();
  const getTicketParams = id
    ? { id: Uuid(id) }
    : { id: Uuid("00000000-0000-0000-0000-000000000000") };
  const ticketQuery = useClientManager("support")
    .getTicket()
    .useQuery(getTicketParams, { enabled: !!id });

  return (
    <>
      <QuerySkeleton query={ticketQuery} height="53px" width="100%">
        {(ticket: TicketDetailDto) => (
          <>
            <HeaderBar
              header={`${ticket.subject.trim() || ticket.ticketNumber} - ${ticket.ticketNumber}`}
              actions={<SupportTicketHeaderActions ticket={ticket} />}
              onGoBack={() => navigate(listPath)}
            />
          </>
        )}
      </QuerySkeleton>
      <Page
        css={{
          marginTop: isMobile ? "53px" : "83px",
          padding: isMobile ? "0 1rem" : "0 2rem",
          marginBottom: "2rem",
        }}
      >
        <QuerySkeleton query={ticketQuery}>
          {(ticket: TicketDetailDto) => {
            const reporterCustomerUuid = getReporterCustomerUuidFromTicket(ticket);
            return (
            <div
              css={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                gap: isMobile ? "1rem" : "2rem",
              }}
            >
              {isMobile ? (
                <>
                  <div
                    css={{
                      display: "flex",
                      flexDirection: "column",
                      flex: 1,
                      minWidth: 0,
                      gap: "1rem",
                      marginTop: "0.75rem",
                    }}
                  >
                    <SupportTicketMetaSection ticket={ticket} listPath={listPath} />
                  </div>
                  <div
                    css={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "32px",
                    }}
                  >
                    <SectionHeader isHr>Dane klienta</SectionHeader>
                    <SupportTicketContactCrmCard
                      ticketId={ticket.id}
                      subject={ticket.subject}
                      status={ticket.status}
                      priority={ticket.priority}
                      category={ticket.category}
                      invoiceNumber={ticket.invoiceNumber ?? null}
                      orderNumber={ticket.orderNumber ?? null}
                      reporterName={ticket.reporterName}
                      reporterEmail={ticket.reporterEmail}
                      ticketCustomers={ticket.ticketCustomers}
                      tags={ticket.tags}
                      products={ticket.products}
                    />
                    <SupportTicketActiveCartCard crmCustomerUuid={reporterCustomerUuid ?? null} />
                    <SupportTicketMessagesAndNotes ticket={ticket} />
                  </div>
                </>
              ) : (
                <>
                  <div
                    css={{
                      display: "flex",
                      flexDirection: "column",
                      flex: "1 1 auto",
                      gap: "32px",
                    }}
                  >
                    <SupportTicketContactCrmCard
                      ticketId={ticket.id}
                      subject={ticket.subject}
                      status={ticket.status}
                      priority={ticket.priority}
                      category={ticket.category}
                      invoiceNumber={ticket.invoiceNumber ?? null}
                      orderNumber={ticket.orderNumber ?? null}
                      reporterName={ticket.reporterName}
                      reporterEmail={ticket.reporterEmail}
                      ticketCustomers={ticket.ticketCustomers}
                      tags={ticket.tags}
                      products={ticket.products}
                    />
                    <SupportTicketActiveCartCard crmCustomerUuid={reporterCustomerUuid ?? null} />
                    <SupportTicketMessagesAndNotes ticket={ticket} />
                  </div>
                  <div
                    css={{
                      display: "flex",
                      flexDirection: "column",
                      flex: 1,
                      minWidth: "19rem",
                      gap: "1rem",
                    }}
                  >
                    <SupportTicketMetaSection ticket={ticket} listPath={listPath} />
                  </div>
                </>
              )}
            </div>
            );
          }}
        </QuerySkeleton>
      </Page>
    </>
  );
};
